import { z } from 'zod';
import { isoDateStringSchema, objectIdSchema, paginationQuerySchema } from './common.js';

/**
 * ASK A QUERY — the way somebody in a shop reaches a human being.
 *
 * The tutorials beside this answer "how do I do X". This answers the other half, which is
 * everything else: a figure that looks wrong, a screen that will not do what they expect, a
 * question about the bill. Without it the only channel is a phone number they have to find,
 * during hours they are usually working, and the realistic alternative to asking is quietly
 * not using the feature.
 *
 * Three decisions shape the shape below.
 *
 * **A query is a THREAD, not a form submission.** The first useful reply to most of these is
 * a question back, and a support channel where the answer arrives somewhere the question was
 * not is a channel people stop using after one round.
 *
 * **It captures where they were.** `route` and `appVersion` ride along, filled in by the
 * client, because "it does not work" from a screen we cannot name costs two messages to
 * resolve and one of them is ours.
 *
 * **Nothing here is private to us.** The thread lives in the shop's own tenant and the shop
 * reads all of it. A support system a customer cannot read their own half of is one they
 * cannot check the status of, which produces the phone call it was meant to prevent.
 */

/* -------------------------------------------------------------------------------------------
 * What kind of question it is
 * ---------------------------------------------------------------------------------------- */

/**
 * The menu exists so answers can be routed and counted. It is short on purpose: a taxonomy
 * with fourteen entries is one where everybody picks the first option.
 */
export const SUPPORT_CATEGORIES = [
  'how_do_i',
  'something_wrong',
  'billing',
  'feature_request',
  'other',
] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];
export const supportCategorySchema = z.enum(SUPPORT_CATEGORIES);

/**
 * Where a query has got to.
 *
 * `waiting_on_you` is the state that earns its place: a query we have answered with a
 * question of our own is neither open nor closed, and a shop that cannot see the difference
 * assumes we have gone quiet on them.
 */
export const SUPPORT_STATUSES = ['open', 'answered', 'waiting_on_you', 'closed'] as const;
export type SupportStatus = (typeof SUPPORT_STATUSES)[number];
export const supportStatusSchema = z.enum(SUPPORT_STATUSES);

/** Still ours to answer. What the shop's own list defaults to showing. */
export function isOpenQuery(status: SupportStatus): boolean {
  return status !== 'closed';
}

/* -------------------------------------------------------------------------------------------
 * Asking
 * ---------------------------------------------------------------------------------------- */

export const SUPPORT_SUBJECT_MAX_LENGTH = 160;
export const SUPPORT_MESSAGE_MAX_LENGTH = 4000;

export const supportSubjectSchema = z
  .string()
  .trim()
  .min(1, 'Say in a few words what this is about')
  .max(SUPPORT_SUBJECT_MAX_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

export const supportMessageSchema = z
  .string()
  .trim()
  .min(1, 'Write your question')
  .max(SUPPORT_MESSAGE_MAX_LENGTH);

export const createSupportQuerySchema = z.object({
  subject: supportSubjectSchema,
  message: supportMessageSchema,
  category: supportCategorySchema.default('how_do_i'),
  /**
   * The screen they were on. Filled in by the client, never typed, and optional so that a
   * query raised from anywhere still works.
   */
  route: z.string().trim().max(200).nullish(),
  /**
   * How they would rather be reached. Absent means the phone number on their account, which
   * is the honest default in this market — nobody here is waiting by an inbox.
   */
  contactPhone: z.string().trim().max(20).nullish(),
});
export type CreateSupportQueryInput = z.input<typeof createSupportQuerySchema>;

/** A follow-up on a thread that already exists. */
export const replyToSupportQuerySchema = z.object({
  message: supportMessageSchema,
});
export type ReplyToSupportQueryInput = z.input<typeof replyToSupportQuerySchema>;

/* -------------------------------------------------------------------------------------------
 * The thread
 * ---------------------------------------------------------------------------------------- */

/** Who wrote one message. `support` is us; `shop` is them. */
export const SUPPORT_AUTHORS = ['shop', 'support'] as const;
export type SupportAuthor = (typeof SUPPORT_AUTHORS)[number];
export const supportAuthorSchema = z.enum(SUPPORT_AUTHORS);

export const supportMessageViewSchema = z.object({
  author: supportAuthorSchema,
  /** The person's name, so a shop with four people can see which of them asked. */
  authorName: z.string().nullable(),
  body: z.string(),
  sentAt: isoDateStringSchema,
});
export type SupportMessageView = z.infer<typeof supportMessageViewSchema>;

export const supportQuerySchema = z.object({
  id: objectIdSchema,
  /** `Q-000147`. Short enough to read down a phone line. */
  reference: z.string(),
  subject: z.string(),
  category: supportCategorySchema,
  status: supportStatusSchema,
  route: z.string().nullable(),
  contactPhone: z.string().nullable(),
  raisedByUserId: z.string().nullable(),
  raisedByName: z.string().nullable(),
  messages: z.array(supportMessageViewSchema),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
  /** When we last said something. Null while nobody has answered yet. */
  lastRepliedAt: isoDateStringSchema.nullable(),
  closedAt: isoDateStringSchema.nullable(),
});
export type SupportQuery = z.infer<typeof supportQuerySchema>;

export const supportQuerySearchSchema = paginationQuerySchema.extend({
  status: supportStatusSchema.optional(),
  /** Everything not closed — the list's default. */
  openOnly: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value) => value === true || value === 'true' || value === '1'),
});
export type SupportQuerySearch = z.infer<typeof supportQuerySearchSchema>;

export const supportQueryPageSchema = z.object({
  items: z.array(supportQuerySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type SupportQueryPage = z.infer<typeof supportQueryPageSchema>;

/* -------------------------------------------------------------------------------------------
 * Reaching a person directly
 * ---------------------------------------------------------------------------------------- */

/**
 * How to reach support without the app — the phone number, the hours and the WhatsApp link.
 *
 * Served rather than hard-coded in the browser because it is OURS, not the shop's: it changes
 * when we change our hours, and a number baked into a bundle is a number that is wrong for
 * everybody still running last month's build.
 *
 * Every field is nullable. A deployment that has not configured a support number must render
 * a screen with the query form and no phone row, not a screen with a blank phone row.
 */
export const supportContactSchema = z.object({
  phone: z.string().nullable(),
  whatsappUrl: z.string().nullable(),
  email: z.string().nullable(),
  /** Free text, because "10am–7pm, closed Sunday" does not fit a structured shape usefully. */
  hours: z.string().nullable(),
  address: z.string().nullable(),
});
export type SupportContact = z.infer<typeof supportContactSchema>;
